import React, { useState, useEffect, useRef } from 'react';
import {
  Video,
  Camera,
  Mic,
  Volume2,
  Sparkle,
  Sparkles,
  SpellCheck,
  Check,
  ChevronDown,
  AlertOctagon,
} from 'lucide-react';
import { Modal, Button, List, Typography, Space } from 'antd';
import {
  CheckCircleOutlined,
  UserOutlined,
  EyeOutlined,
  StopOutlined,
} from '@ant-design/icons';
import {
  useNavigate,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
// Adjust path as needed

interface MediaDevice {
  deviceId: string;
  label: string;
}
// Define types for the audio devices
interface AudioDevice {
  label: string;
}

const { Title, Text } = Typography;
const DeviceSelector = () => {
  //check if encryptid is in the params
  const [searchParams] = useSearchParams();
  const encryptId = searchParams.get('encryptId'); // Get encryptId from query params
  const [studentId, setStudentId] = useState<string>('');

  useEffect(() => {
    if (encryptId) {
      setStudentId(encryptId);
    }
  }, [encryptId]);

  if (!encryptId) {
    return (
      <div className="flex items-center justify-center min-h-screen text-xl font-bold text-[#022c22]">
        No student ID found
      </div>
    );
  }

  //all other stuff after finding encryptid

  const [videoDevices, setVideoDevices] = useState<MediaDevice[]>([]);
  const [audioDevices, setAudioDevices] = useState<MediaDevice[]>([]);
  const [audioOutputs, setAudioOutputs] = useState<MediaDevice[]>([]);
  const [selectedVideo, setSelectedVideo] = useState<string>('');
  const [selectedAudio, setSelectedAudio] = useState<string>('');
  const [selectedOutput, setSelectedOutput] = useState<string>('');
  const [hasCameraAccess, setHasCameraAccess] = useState(false);
  const [hasAudioAccess, setHasAudioAccess] = useState(false);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isDevicesReady, setIsDevicesReady] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioTestRef = useRef<HTMLAudioElement>(null);
  const navigate = useNavigate();

  const [isVideoOn, setIsVideoOn] = useState(false);
  const [isOpen, setIsOpen] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { Title, Text } = Typography;

  const proctoringRules = [
    {
      icon: <img src="/smily.png"></img>,
      text: 'Speak clearly and make sure your face is visible during the interview.',
    },
    {
      icon: <img src="/smily2.png"></img>,
      text: 'Stay within the camera frame for better quality.',
    },
    {
      icon: <img src="/smily3.png"></img>,
      text: 'Avoid any interruptions or background noise while interviewing.',
    },
  ];

  const successTips = [
    'Dress professionally, just like you would for an actual interview.',
    'Keep a calm posture and maintain eye contact with the camera.',
    'Take your time to think before you answer.',
    'Stay focused and avoid unnecessary distractions.',
  ];

  useEffect(() => {
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      navigator.mediaDevices
        .getUserMedia({ video: true, audio: true }) // Request both video and audio
        .then((stream) => {
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }
        })
        .catch((err) => {
          console.error('Error accessing media devices: ', err);
        });
    }
  }, []);

  const [deviceErrors, setDeviceErrors] = useState({
    camera: false,
    microphone: false,
  });

  const initializeDevices = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();

      const videoInputs = devices
        .filter((device) => device.kind === 'videoinput')
        .map((device) => ({
          deviceId: device.deviceId,
          label: device.label || `Camera ${device.deviceId.slice(0, 5)}`,
        }));

      const audioInputs = devices
        .filter((device) => device.kind === 'audioinput')
        .map((device) => ({
          deviceId: device.deviceId,
          label: device.label || `Microphone ${device.deviceId.slice(0, 5)}`,
        }));

      const audioOutputs = devices
        .filter((device) => device.kind === 'audiooutput')
        .map((device) => ({
          deviceId: device.deviceId,
          label: device.label || `Speaker ${device.deviceId.slice(0, 5)}`,
        }));

      setVideoDevices(videoInputs);
      setAudioDevices(audioInputs);
      setAudioOutputs(audioOutputs);

      // Check if devices are actually available
      setDeviceErrors({
        camera: videoInputs.length === 0,
        microphone: audioInputs.length === 0,
      });

      if (videoInputs.length && audioInputs.length) {
        setSelectedVideo(videoInputs[0].deviceId);
        setSelectedAudio(audioInputs[0].deviceId);
        await startStream(videoInputs[0].deviceId, audioInputs[0].deviceId);
      }

      if (audioOutputs.length) {
        setSelectedOutput(audioOutputs[0].deviceId);
      }
    } catch (error) {
      console.error('Error initializing devices:', error);
      setDeviceErrors({
        camera: true,
        microphone: true,
      });
      setHasCameraAccess(false);
      setHasAudioAccess(false);
      setIsDevicesReady(false);
    }
  };

  const startStream = async (videoId: string, audioId: string) => {
    try {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }

      // Try to get video stream first
      let videoStream: MediaStream | null = null;
      try {
        videoStream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: videoId },
        });
        setHasCameraAccess(true);
        setDeviceErrors((prev) => ({ ...prev, camera: false }));
      } catch (error) {
        console.error('Error accessing camera:', error);
        setHasCameraAccess(false);
        setDeviceErrors((prev) => ({ ...prev, camera: true }));
      }

      // Try to get audio stream
      let audioStream: MediaStream | null = null;
      try {
        audioStream = await navigator.mediaDevices.getUserMedia({
          audio: { deviceId: audioId },
        });
        setHasAudioAccess(true);
        setDeviceErrors((prev) => ({ ...prev, microphone: false }));
      } catch (error) {
        console.error('Error accessing microphone:', error);
        setHasAudioAccess(false);
        setDeviceErrors((prev) => ({ ...prev, microphone: true }));
      }

      // Combine streams if both are available, or use whatever we got
      const tracks: MediaStreamTrack[] = [];
      if (videoStream) tracks.push(...videoStream.getTracks());
      if (audioStream) tracks.push(...audioStream.getTracks());

      const combinedStream = new MediaStream(tracks);
      setStream(combinedStream);

      if (videoRef.current && videoStream) {
        videoRef.current.srcObject = combinedStream;
      }

      setIsDevicesReady(Boolean(videoStream && audioStream));
      setIsVideoOn(Boolean(videoStream));
    } catch (error) {
      console.error('Error starting stream:', error);
      setHasCameraAccess(false);
      setHasAudioAccess(false);
      setIsDevicesReady(false);
      setIsVideoOn(false);
    }
  };

  const setAudioOutput = (deviceId: string) => {
    if (audioTestRef.current) {
      const audioElement = audioTestRef.current as any;
      if (typeof audioElement.setSinkId === 'function') {
        audioElement
          .setSinkId(deviceId)
          .then(() => {
            // testSpeaker();
          })
          .catch((error: Error) => {
            console.error('Error setting audio output device:', error);
          });
      }
    }
  };

  useEffect(() => {
    initializeDevices();
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  useEffect(() => {
    if (selectedOutput) {
      setAudioOutput(selectedOutput);
    }
  }, [selectedOutput]);

  const handleNextClick = () => {
    const jobId = searchParams.get('job_id'); // read from query params

    if (isDevicesReady) {
      if (studentId) {
        // ✅ Build the URL dynamically
        const targetUrl = jobId
          ? `/interview/${studentId}?job_id=${jobId}`
          : `/interview/${studentId}`;

        navigate(targetUrl);
      } else {
        Modal.warning({
          title: 'Missing Information',
          content: 'Student ID is missing from the URL.',
        });
      }
    } else {
      Modal.warning({
        title: 'Device Setup Required',
        content:
          'Please ensure all devices are properly configured before starting.',
      });
    }
  };

  const [micLevel, setMicLevel] = useState(0); // State to track mic level
  const mediaStreamRef = useRef<MediaStream | null>(null);
  useEffect(() => {
    // Initialize microphone and audio context
    const initializeAudio = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        mediaStreamRef.current = stream;

        const AudioContext =
          window.AudioContext || (window as any).webkitAudioContext;
        const audioContext = new AudioContext();
        const analyser = audioContext.createAnalyser();
        const source = audioContext.createMediaStreamSource(stream);

        analyser.fftSize = 256;
        const dataArray = new Uint8Array(analyser.frequencyBinCount);

        source.connect(analyser);

        // Save references to clean up later
        audioContextRef.current = audioContext;
        analyserRef.current = analyser;
        dataArrayRef.current = dataArray;

        // Function to calculate the mic level
        const calculateMicLevel = () => {
          if (analyserRef.current && dataArrayRef.current) {
            analyser.getByteFrequencyData(dataArray);
            const average =
              dataArray.reduce((sum, value) => sum + value, 0) /
              dataArray.length;
            setMicLevel(Math.min(average, 100)); // Normalize the mic level to 0-100
            setVolume(average);
            setIsSpeaking(average > 20); // Update speaking state based on threshold
          }
          requestAnimationFrame(calculateMicLevel);
        };

        calculateMicLevel();
      } catch (err) {
        console.error('Error accessing microphone:', err);
      }
    };

    initializeAudio();

    return () => {
      // Safely clean up the audio context
      if (audioContextRef.current) {
        const audioContext = audioContextRef.current;
        if (audioContext.state !== 'closed') {
          audioContext.close().catch((err) => {
            console.error('Error closing AudioContext:', err);
          });
        }
      }

      // Stop all media tracks
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      }

      // Reset references
      analyserRef.current = null;
      dataArrayRef.current = null;
      audioContextRef.current = null;
      mediaStreamRef.current = null;
      setIsSpeaking(false);
    };
  }, []);

  const [check, setCheck] = useState(false);
  const [checkaudio, setCheckaudio] = useState(false);
  const [checkvedio, setCheckvedio] = useState(false);

  //working here

  const [selectedDevice, setSelectedDevice] = useState<AudioDevice | null>(
    null,
  );

  const [isFocused, setIsFocused] = useState(false);
  const [checkOutput, setCheckOutput] = useState(false);
  const [checkVideo, setCheckVideo] = useState(false);
  const [selectedOutputDevice, setSelectedOutputDevice] =
    useState<MediaDevice | null>(null);
  const [selectedVideoDevice, setSelectedVideoDevice] =
    useState<MediaDevice | null>(null);

  const handleOutputDeviceSelect = (dev: MediaDevice) => {
    setSelectedOutputDevice(dev);
    setSelectedOutput(dev.deviceId);
    setCheckOutput(false);
    setAudioOutput(dev.deviceId);
  };

  const handleVideoDeviceSelect = (dev: MediaDevice) => {
    setSelectedVideoDevice(dev);
    setSelectedVideo(dev.deviceId);
    setCheckVideo(false);
    startStream(dev.deviceId, selectedAudio);
  };

  const handleClick = () => {
    setCheck((prevCheck) => !prevCheck);
    setCheckOutput(false);
    setCheckVideo(false);
  };

  const handleOutputClick = () => {
    setCheckOutput((prevCheckOutput) => !prevCheckOutput);
    setCheck(false);
    setCheckVideo(false);
  };

  const handleVideoClick = () => {
    setCheckVideo((prevCheckVideo) => !prevCheckVideo);
    setCheck(false);
    setCheckOutput(false);
  };

  const handleDeviceSelect = (dev: AudioDevice) => {
    setSelectedDevice(dev);
  };

  const [isSoundTesting, setIsSoundTesting] = useState(false);

  const testSpeaker = () => {
    if (!audioTestRef.current) {
      console.error('Audio reference is not available.');
      alert('Audio element is not initialized.');
      return;
    }

    audioTestRef.current
      .play()
      .then(() => {})
      .catch((error) => {
        console.error('Error playing audio:', error);
        alert(
          'Unable to play audio. Ensure the file exists, user interaction occurred, and the audio device is functioning.',
        );
      });
  };

  const isAnimating = micLevel > 10;

  useEffect(() => {
    if (audioDevices.length > 0 && !selectedDevice) {
      handleDeviceSelect(audioDevices[0]);
    }

    if (audioOutputs.length > 0 && !selectedOutputDevice) {
      handleOutputDeviceSelect(audioOutputs[0]);
    }

    if (videoDevices.length > 0 && !selectedVideoDevice) {
      handleVideoDeviceSelect(videoDevices[0]);
    }
  }, [audioDevices, audioOutputs, videoDevices]);

  //working here
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [volume, setVolume] = useState(0); // Track microphone volume
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);

  const startListening = async () => {
    try {
      // Request microphone access with noise suppression and echo cancellation
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          noiseSuppression: true, // Enable noise suppression
          echoCancellation: true, // Enable echo cancellation
          autoGainControl: true, // Automatically adjust microphone gain
        },
      });
      const audioContext = new (
        window.AudioContext || (window as any).webkitAudioContext
      )();
      const analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(stream);

      source.connect(analyser);
      analyser.fftSize = 256; // Set frequency data size
      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      // Save references
      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      dataArrayRef.current = dataArray;

      setIsSpeaking(true);

      // Analyze audio input
      const analyze = () => {
        if (!analyserRef.current || !dataArrayRef.current) return;
        analyserRef.current.getByteFrequencyData(dataArrayRef.current);
        const averageVolume =
          dataArrayRef.current.reduce((a, b) => a + b, 0) /
          dataArrayRef.current.length;
        setVolume(averageVolume);
        requestAnimationFrame(analyze);
      };
      analyze();
    } catch (error) {
      console.error('Error accessing microphone:', error);
    }
  };

  const stopListening = () => {
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    dataArrayRef.current = null;
    setIsSpeaking(false);
    setVolume(0);
  };

  useEffect(() => {
    startListening(); // Automatically start listening on mount

    return () => stopListening(); // Clean up on component unmount
  }, []);

  return (
    <div className="flex bg-white">
      <Modal
        open={isOpen}
        centered
        width={650} // Fixed width
        footer={[
          <Button
            key="submit"
            type="primary"
            style={{
              borderRadius: '8px',
              backgroundColor: '#022c22',
              border: 'none',
              padding: '0.5rem 1.5rem',
            }}
            onClick={() => {
              setIsOpen(false);
            }}
          >
            Got it
          </Button>,
        ]}
        onCancel={() => setIsOpen(false)}
        bodyStyle={{
          height: 500, // Fixed height
          padding: '1rem',
          borderRadius: '12px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Title */}
          <Title
            level={4}
            style={{
              textAlign: 'center',
              color: '#1a1a1a',
            }}
          >
            Quick Instructions
          </Title>

          {/* Interview Rules Section */}
          <div
            style={{
              padding: '1rem',
              background: '#F3F3F3',
              borderRadius: '10px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            }}
          >
            <Title
              level={4}
              style={{
                marginBottom: '0.5rem',
                color: '#437AFF',
                textAlign: 'left',
              }}
            >
              Interview Rules
            </Title>
            <List
              dataSource={proctoringRules}
              renderItem={(item) => (
                <List.Item style={{ padding: '4px 0' }}>
                  <Space size="small">
                    <span
                      style={{
                        fontSize: '1.2rem',
                        color: '#1890ff',
                        display: 'inline-flex',
                        alignItems: 'center',
                      }}
                    >
                      {item.icon}
                    </span>
                    <Text
                      style={{
                        fontSize: '14px',
                        color: '#595959',
                      }}
                    >
                      {item.text}
                    </Text>
                  </Space>
                </List.Item>
              )}
              split={false}
            />
          </div>

          {/* Pro Tips Section */}
          <div
            style={{
              padding: '1rem',
              background: '#F3F3F3',
              borderRadius: '10px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            }}
          >
            <Title
              level={4}
              style={{
                marginBottom: '0.5rem',
                color: '#437AFF',
                textAlign: 'left',
              }}
            >
              Pro Tips
            </Title>
            <List
              dataSource={successTips}
              renderItem={(item) => (
                <List.Item style={{ padding: '4px 0' }}>
                  <Space size="small">
                    <CheckCircleOutlined
                      style={{
                        color: '#52c41a',
                        fontSize: '1.2rem',
                        display: 'inline-flex',
                        alignItems: 'center',
                      }}
                    />
                    <Text
                      style={{
                        fontSize: '14px',
                        color: '#595959',
                      }}
                    >
                      {item}
                    </Text>
                  </Space>
                </List.Item>
              )}
              split={false}
            />
          </div>
        </Space>
      </Modal>

      <Modal
        title={
          <div className="text-center w-full text-xl font-semibold">
            How it works?
          </div>
        }
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={[
          <button
            className="bg-gradient-to-r from-[#022c22] to-[#022c22] bg-[#022c22] text-white rounded-lg px-6 py-1 font-semibold border-none"
            onClick={() => setIsModalOpen(false)}
          >
            Got it
          </button>,
        ]}
        centered
        width={650}
      >
        <div className="space-y-6 bg-white">
          <div className="flex items-start space-x-3">
            <span className="bg-blue-500 text-white font-bold rounded-full p-2 w-6 h-6 flex items-center justify-center">
              1
            </span>
            <div>
              <h3 className="text-lg font-semibold">Why AI-interviews?</h3>
              <ul className="list-disc pl-5 text-[#818181]">
                <li className=" text-md">
                  Our interactive AI understands your unique experiences and
                  helps you showcase skills that employers are actually looking
                  for.
                </li>
                <li className=" text-md">
                  No generic questions – just meaningful conversations about
                  your journey and Unbiased outcomes.
                </li>
              </ul>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <span className="bg-blue-500 text-white font-bold p-2 rounded-full w-6 h-6 flex items-center justify-center">
              2
            </span>
            <div>
              <h3 className="text-lg font-semibold">How will it happen?</h3>
              <ul className="list-disc pl-5 text-[#818181]">
                <li className=" text-md">
                  Upload your resume, and take a 15-minute interview with our
                  AI. It is that simple. Our AI analyses your resume to create
                  personalised questions, giving you the perfect platform to
                  demonstrate your capabilities and build confidence with each
                  response.
                </li>
              </ul>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <span className="bg-blue-500 text-white p-2 font-bold rounded-full w-6 h-6 flex items-center justify-center">
              3
            </span>
            <div>
              <h3 className="text-lg font-semibold">The Outcome</h3>
              <ul className="list-disc pl-5 text-[#818181]">
                <li className=" text-md">
                  Get personalized feedback on 4 parameters: Technical accuracy,
                  clarity and structure, depth of knowledge, relevancy.
                </li>
                <li className=" text-md">
                  Plus, get an overall score to understand exactly where you
                  stand.
                </li>
                <li className=" text-md">Ready to impress your recruiters?</li>
              </ul>
            </div>
          </div>
        </div>
      </Modal>

      <div className=" grid grid-cols-10 w-full">
        {/*Right section here*/}

        <div className=" col-span-7 bg-white">
          <div className="flex flex-col items-center justify-center min-h-screen ">
            <div className="w-full max-w-2xl  p-4 ">
              <div className="absolute top-5 space-y-1 p-6 flex flex-col justify-center ">
                <div className=" flex space-x-2 ">
                  <img src="/round.png"></img>
                  <h1 className=" text-2xl font-bold">Self Assessment</h1>
                </div>
                <div>
                  <p className=" text-sm  text-record ">
                    AI-powered self assessment interview with insights to
                    sharpen your skills and confidence.
                  </p>
                </div>
              </div>
              <div>
                <div className="relative w-full max-h-[80vh] aspect-video bg-black rounded-xl overflow-hidden mb-4">
                  {/* Animation for moving the dots according for human inputs*/}

                  <div className="flex bg-blue-600 absolute left-4 bottom-3 rounded-full justify-center items-center w-8 h-8 p-1 z-50">
                    {/* Left Dot */}
                    <motion.div
                      className="w-1 bg-white rounded-full"
                      animate={{
                        height: isSpeaking
                          ? `${Math.min(volume / 40, 0.8)}rem`
                          : '0.3rem', // Smaller animation
                      }}
                      transition={{
                        duration: 0.1, // Smoothly adjust scale and height
                      }}
                    ></motion.div>

                    {/* Center Dot */}
                    <motion.div
                      className="w-1 bg-white rounded-full mx-1"
                      animate={{
                        height: isSpeaking
                          ? `${Math.min(volume / 25, 1.2)}rem`
                          : '0.3rem', // Center dot more prominent
                      }}
                      transition={{
                        duration: 0.1, // Smoothly adjust scale and height
                      }}
                    ></motion.div>

                    {/* Right Dot */}
                    <motion.div
                      className="w-1 bg-white rounded-full"
                      animate={{
                        height: isSpeaking
                          ? `${Math.min(volume / 40, 0.8)}rem`
                          : '0.3rem', // Smaller animation
                      }}
                      transition={{
                        duration: 0.1, // Smoothly adjust scale and height
                      }}
                    ></motion.div>
                  </div>
                  {/* Animation for moving the dots according for human inputs ends here*/}

                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className={`w-full h-full inset-0 object-cover z-10 ${
                      !hasCameraAccess && 'hidden'
                    }`}
                  />

                  {(!hasCameraAccess || deviceErrors.camera) && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#1a1a1a]">
                      <div className="grid grid-cols-12 w-full h-full opacity-10">
                        {[...Array(144)].map((_, i) => (
                          <div
                            key={i}
                            className="border-[0.5px] border-red-500/20"
                          />
                        ))}
                      </div>
                      <div className="absolute bottom-3 flex flex-col items-center space-y-2">
                        <div className="flex items-center space-x-2 bg-[#2d2d2d] px-3 py-1.5 rounded-md">
                          <Camera className="w-5 h-5 text-gray-400" />
                          <span className="text-sm text-gray-300">
                            Camera not working
                          </span>
                        </div>
                        {deviceErrors.microphone && (
                          <div className="flex items-center space-x-2 bg-[#2d2d2d] px-3 py-1.5 rounded-md">
                            <Mic className="w-5 h-5 text-gray-400" />
                            <span className="text-sm text-gray-300">
                              Microphone not detected
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex relative float-start justify-center items-center space-x-2">
                  <div>
                    <div className="flex justify-center items-center rounded-lg pl-2 pr-2">
                      <img
                        src="/mic.png"
                        alt="Microphone"
                        className=" w-8 h-8"
                      />
                      <button
                        onClick={handleClick}
                        className="flex items-center justify-between hover:bg-inside pl-3 pr-3 py-2 rounded-2xl space-x-2"
                      >
                        <p className="text-sm">
                          {selectedDevice
                            ? selectedDevice.label.slice(0, 10)
                            : 'Select audio'}
                        </p>
                        <ChevronDown className="w-4 h-4 text-gray-300" />
                      </button>
                      {check && (
                        <div className="bg-white rounded-md absolute w-auto bottom-14 left-12 p-7">
                          {audioDevices.map((dev) => (
                            <div
                              key={dev.label}
                              className="flex items-center h-auto p-4 space-x-2 justify-between hover:bg-gray-50 cursor-pointer"
                              onClick={() => handleDeviceSelect(dev)}
                            >
                              <p
                                className={`text-sm truncate ${
                                  selectedDevice &&
                                  selectedDevice.label === dev.label
                                    ? 'text-blueai'
                                    : 'text-black'
                                }`}
                              >
                                {dev.label}
                              </p>

                              {selectedDevice &&
                                selectedDevice.label === dev.label && (
                                  <div className="w-5 h-5 rounded-full bg-blueai flex items-center justify-center ml-4 absolute left-0">
                                    <Check className="h-3 w-3 text-white" />
                                  </div>
                                )}
                            </div>
                          ))}
                          {selectedDevice && (
                            <div className="flex flex-col items-center p-4 ">
                              <div className="flex items-center space-x-3 w-full ">
                                <Mic className="h-6 w-6 text-black absolute left-3" />
                                <div className="flex-1 h-1.5 bg-gray-300 rounded-full overflow-hidden">
                                  <div
                                    id="mic-bar"
                                    className="h-1.5 bg-blueai rounded-full flex absolute left-12  transition-0.1s ease-in-out"
                                    style={{ width: `${micLevel}%` }}
                                  ></div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-center items-center rounded-lg pl-2 pr-2">
                      <img
                        src="/audio.png"
                        alt="Speaker"
                        className=" w-8 h-8"
                      />
                      <button
                        onClick={handleOutputClick}
                        className="flex items-center justify-between hover:bg-inside pl-3 pr-3 py-2 rounded-2xl space-x-2"
                      >
                        <p className="text-sm">
                          {selectedOutputDevice
                            ? selectedOutputDevice.label.slice(0, 10)
                            : 'Select Speaker'}
                        </p>
                        <ChevronDown className="w-4 h-4 text-gray-600" />
                      </button>
                      {checkOutput && (
                        <div className="bg-white rounded-md absolute w-auto bottom-14 left-36 p-7">
                          {audioOutputs.map((dev) => (
                            <div
                              key={dev.deviceId}
                              className="flex items-center h-auto p-4 space-x-2 justify-between hover:bg-gray-50 cursor-pointer"
                              onClick={() => handleOutputDeviceSelect(dev)}
                            >
                              <p
                                className={`text-sm truncate ${
                                  selectedOutputDevice &&
                                  selectedOutputDevice.label === dev.label
                                    ? 'text-blueai'
                                    : 'text-black'
                                }`}
                              >
                                {dev.label}
                              </p>
                              {selectedOutputDevice &&
                                selectedOutputDevice.deviceId ===
                                  dev.deviceId && (
                                  <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center absolute left-0 justify-center ml-4">
                                    <Check className="h-3 w-3 text-white" />
                                  </div>
                                )}
                            </div>
                          ))}
                          <hr />
                          <div className="flex items-center ml-4 ">
                            <Volume2 color="#5F6367" className="w-4" />
                            <button
                              onClick={testSpeaker}
                              className="w-auto p-1 text-gray-600 hover:text-gray-800"
                            >
                              Test speakers
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-center items-center rounded-lg pl-2 pr-2">
                      <img
                        src="/camera.png"
                        alt="Camera"
                        className=" w-8 h-8"
                      />
                      <button
                        onClick={handleVideoClick}
                        className="flex items-center justify-between hover:bg-inside pl-3 pr-3 py-2 rounded-2xl space-x-2"
                      >
                        <p className="text-sm">
                          {selectedVideoDevice
                            ? selectedVideoDevice.label.slice(0, 10)
                            : 'Select Camera'}
                        </p>
                        <ChevronDown className="w-4 h-4 text-gray-600" />
                      </button>
                      {videoDevices.length > 0 && checkVideo && (
                        <div className="bg-white  rounded-md absolute w-auto bottom-14 right-0 p-7">
                          {videoDevices.map((dev) => (
                            <div
                              key={dev.deviceId}
                              className="flex items-center h-auto p-4  space-x-2 justify-between hover:bg-gray-50 cursor-pointer"
                              onClick={() => handleVideoDeviceSelect(dev)}
                            >
                              <p
                                className={`text-sm truncate ${
                                  selectedVideoDevice &&
                                  selectedVideoDevice.label === dev.label
                                    ? 'text-blueai'
                                    : 'text-black'
                                }`}
                              >
                                {dev.label}
                              </p>
                              {selectedVideoDevice &&
                                selectedVideoDevice.deviceId ===
                                  dev.deviceId && (
                                  <div className="w-5 h-5 rounded-full bg-blue-600 absolute left-0 flex items-center justify-center ml-4">
                                    <Check className="h-3 w-3 text-white" />
                                  </div>
                                )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/*Left section here*/}

        <div className="col-span-3 flex justify-start items-center bg-white">
          <div className="flex items-center justify-center ">
            <div className="space-y-3">
              <div className="flex flex-col justify-center items-center space-y-1">
                <h1 className=" font-semibold leading-4 text-lg">
                  Ready to Join?
                </h1>
                <img
                  src="/GoodFreshersIcon.png"
                  alt="GoodFreshers AI"
                  className="w-32 h-28"
                />
                <div className="flex justify-center items-center space-x-2">
                  <Sparkles color="#5F6367" className="w-4" />
                  <p className="text-sm text-record">
                    GoodFreshers AI has joined
                  </p>
                </div>
                {isDevicesReady ? (
                  <button
                    className="bg-[#022c22] text-white px-12 !py-3 rounded-full ml-4"
                    onClick={handleNextClick}
                  >
                    Start interview
                  </button>
                ) : (
                  <button className="bg-[#BFBFBF] text-white px-12 !py-3 rounded-full ml-4">
                    Start interview
                  </button>
                )}
              </div>

              <div className="flex justify-start items-center space-x-2">
                <button
                  className="bg-white border text-main px-6 py-2 ml-10 rounded-full"
                  onClick={() => setIsModalOpen(true)}
                >
                  How it works?
                </button>
              </div>
              <div>
                <audio ref={audioTestRef} src="/testingspeaker.mp3" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeviceSelector;
